import React, { useState } from 'react';
import { getThemeProp } from 'styles/theme';
import { withTheme } from 'styled-components';
import ErrorBoundary from './ErrorBoundary';
import Error from 'components/Error';
import IconSpinner from 'components/Icons/IconSpinner';
import {
  ChatResponder,
  Notifications,
  TextMessage,
  ThreadMessages,
} from 'types';
import {
  SChatContentContainer,
  SChatOuterContainer,
} from './ChatContent.styled';
import { useScrollPosition } from '../../../hooks/useScrollPosition';
import Messages from './Messages';
import { useSelector } from 'react-redux';
import {
  getIsAgentConnected,
  getIsAgentConnecting,
  getIsAgentTimeout,
  getSessionExpired,
} from 'store/services/chat/selectors';
import AgentConnectingNotification from './AgentConnectingNotification';

export interface ChatProps {
  isLoaded: boolean;
  isStarting: boolean;
  isAsking: boolean;
  isRetrying: boolean;
  hasError: boolean;
  notificationsConfig: Notifications;
  showDownload: boolean;
  thread: ThreadMessages[];
  theme: any;
  title: string;
  backgroundColor?: string;
  agent: ChatResponder;
  bot: ChatResponder;
  files: File[];
  sendMessage: (message: TextMessage) => Promise<void>;
  resendMessage: (id: string) => Promise<void>;
  restartChat: () => Promise<void>;
  uploadFile: () => void;
}

const ChatContent = ({
  isLoaded,
  isStarting,
  isAsking,
  isRetrying,
  hasError,
  showDownload,
  thread,
  theme,
  title,
  backgroundColor,
  agent,
  bot,
  files,
  sendMessage,
  resendMessage,
  restartChat,
  uploadFile,
}: ChatProps) => {
  const [errorBoundaryKey, setErrorBoundaryKey] = useState(0);

  const isSessionExpired = useSelector(getSessionExpired);
  const isAgentConnecting = useSelector(getIsAgentConnecting);
  const isAgentConnected = useSelector(getIsAgentConnected);
  const isAgentTimeout = useSelector(getIsAgentTimeout);
  const { scrollableContainerRef, triggerScroll } = useScrollPosition({
    hasError,
    thread,
    isAsking,
    showDownload,
    files,
    isSessionExpired,
    isAgentTimeout,
  });

  const showAgentTimeout =
    isAgentTimeout &&
    !isAgentConnecting &&
    !isAgentConnected &&
    !isSessionExpired;

  const handleRestartChat = async () => {
    await restartChat();
    setErrorBoundaryKey(prevKey => prevKey + 1);
  };

  const renderContent = () => {
    if (hasError) {
      return (
        <Error
          restartChat={restartChat}
          theme={theme}
          isRetrying={isRetrying}
        />
      );
    }

    if (isStarting && !isLoaded) {
      return (
        <IconSpinner
          data-test="chat-content-spinner"
          color={getThemeProp('primaryColor', { theme })}
        />
      );
    }

    return (
      <Messages
        thread={thread}
        agent={agent}
        bot={bot}
        sendMessage={sendMessage}
        resendMessage={resendMessage}
        showDownload={showDownload}
        uploadFile={uploadFile}
        restartChat={restartChat}
        isSessionExpired={isSessionExpired}
        showAgentTimeout={showAgentTimeout}
        title={title}
        triggerScroll={triggerScroll}
      />
    );
  };

  return (
    <SChatOuterContainer data-test="chat-content">
      <SChatContentContainer
        ref={scrollableContainerRef}
        isLoaded={isLoaded}
        hasError={hasError}
        backgroundColor={backgroundColor}
      >
        <ErrorBoundary key={errorBoundaryKey} restartChat={handleRestartChat}>
          {renderContent()}
        </ErrorBoundary>
      </SChatContentContainer>
      {isAgentConnecting && <AgentConnectingNotification />}
    </SChatOuterContainer>
  );
};

export default withTheme(ChatContent);
